/** @jsxImportSource @emotion/react */
import { Grid, MenuItem } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { separators } from '../../constants';
import { UserField } from '../../constants/user-labels';
import {
  CountryModel,
  DataFiltersModel,
  FilterPeriodType,
  RangeInput,
  UserRole,
} from '../../models';
import { DefaultModelField } from '../../models/default-model';
import { selectUser } from '../../store/reducers';
import { is } from '../../utils';
import { SGRocketAutocomplete } from '../autocomplete/SGRocketAutocomplete';
import { DateRangePicker } from '../gt-date-picker/DateRangePicker';
import { GtSelect } from '../gt-select/GtSelect';
import { CountryTierAutocomplete } from './components/country-tier-autocomplete/CountryTierAutocomplete';
import { PartnerFilter } from './components/partner-filter/PartnerFilter';

import { StateFilter } from './components/state-filter/StateFilter';
import { PublishersFilter } from './components/users-filter/PublishersFilter';
import { UserFilter } from './components/users-filter/UserFilter';
import {
  hostsPeriodOptions,
  ltvPeriodOptions,
  periodOptions,
  trendsPeriodOptions,
} from './DateFilters.const';
import { useDateFiltersStyles } from './DateFilters.styles';
import { getDatePeriodByType, useSortCountries } from './DateFilters.utils';
import { useSizesByMediaQueries } from './utils/sizes-by-media-queries';

export interface DateFiltersProps {
  title?: string | ReactNode;
  shiftDays?: number;
  isTrends?: boolean;
  isHosts?: boolean;
  isLtv?: boolean;
  filters?: DataFiltersModel;
  androidVersions?: string[];
  appVersions?: string[];
  countries?: CountryModel[];
  disabled?: boolean;
  withFilterByUser?: boolean;
  withFilterByPublisher?: boolean;
  withFilterByPartner?: boolean;
  withFilterByState?: boolean;
  onAndroidVersionsChange?: (filters: DataFiltersModel) => void;
  onAppVersionsChange?: (filters: DataFiltersModel) => void;
  onFiltersChange?: (filters: DataFiltersModel, changedKey?: string) => void;
  initialFiltering?: (filters: DataFiltersModel) => void;
}

export function DateFilters({
  title,
  shiftDays = 0,
  isTrends,
  isHosts,
  isLtv,
  filters,
  androidVersions,
  appVersions,
  countries: countriesList,
  withFilterByUser,
  withFilterByPublisher,
  withFilterByPartner,
  withFilterByState,
  onFiltersChange = () => null,
  onAndroidVersionsChange = () => null,
  onAppVersionsChange = () => null,
  initialFiltering,
  children,
}: PropsWithChildren<DateFiltersProps>): ReactElement {
  const style = useDateFiltersStyles();
  const user = useSelector(selectUser);

  const periodOptionsMap = useMemo(() => {
    if (isLtv) {
      return ltvPeriodOptions;
    }

    if (isTrends) {
      return trendsPeriodOptions;
    }

    if (isHosts) {
      return hostsPeriodOptions;
    }

    return periodOptions;
  }, [isHosts, isTrends]);

  const countries = useSortCountries(countriesList);

  const [period, setPeriod] = useState(() => {
    const isDatePeriodAvailable =
      filters &&
      filters._datePeriod &&
      !!periodOptionsMap.find((item) => item.value === filters._datePeriod);

    return (
      (isDatePeriodAvailable && filters._datePeriod) ||
      (isLtv && periodOptionsMap[0].value) ||
      (isTrends ? trendsPeriodOptions[2].value : periodOptionsMap[1].value)
    );
  });
  const [datePeriods, setDatePeriods] = useState<RangeInput>(
    period === FilterPeriodType.Period
      ? [
          (filters && filters.dateFrom) || null,
          (filters && filters.dateTo) || null,
        ]
      : [null, null],
  );

  useEffect(() => {
    if (!initialFiltering) {
      return;
    }

    let filterByUsers = {};

    if (user && user.role === UserRole.Moderator) {
      filterByUsers = {
        filterByUsers: user[DefaultModelField.Id],
      };
    }

    initialFiltering({
      ...filterByUsers,
      ...(filters || {}),
      ...getDatePeriodByType(period, shiftDays),
    });
  }, []);

  const androidVersionValues = useMemo(() => {
    if (!filters || !filters.androidVersion) {
      return [];
    }

    return is.array(filters.androidVersion)
      ? filters.androidVersion
      : filters.androidVersion.split(separators.comma);
  }, [filters && filters.androidVersion]);

  const appVersionValues = useMemo(() => {
    if (!filters || !filters.appVersion) {
      return [];
    }

    return is.array(filters.appVersion)
      ? filters.appVersion
      : filters.appVersion.split(separators.comma);
  }, [filters && filters.appVersion]);

  const countriesValues = useMemo(() => {
    if (!filters || !filters.country) {
      return [];
    }

    return is.array(filters.country)
      ? filters.country
      : filters.country.split(separators.comma);
  }, [filters && filters.country]);

  const onPeriodChange = (event) => {
    const { value } = event.target;

    setPeriod(value);

    if (value === FilterPeriodType.Period) {
      return;
    }

    if (value === FilterPeriodType.AllTime) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { dateFrom, dateTo, ...otherFilters } = filters;

      return onFiltersChange(otherFilters);
    }

    onFiltersChange({
      ...filters,
      ...getDatePeriodByType(value, shiftDays),
      _datePeriod: value,
    });
  };

  const applyFilterChanges = (changes: DataFiltersModel, changedKey?: string) =>
    onFiltersChange(
      { ...filters, ...changes, _datePeriod: period },
      changedKey,
    );

  const applyAndroidVersionsChange = (changes: DataFiltersModel) =>
    onAndroidVersionsChange({ ...filters, ...changes });
  const applyAppVersionsChange = (changes: DataFiltersModel) =>
    onAppVersionsChange({ ...filters, ...changes });

  const withDatePicker = period === FilterPeriodType.Period;

  const {
    periodSizes,
    rangeSizes,
    userFilterSizes,
    stateFilterSizes,
    // buttonSizes,
  } = useSizesByMediaQueries(
    withFilterByState || !!countries,
    withFilterByUser || withFilterByPublisher,
    withDatePicker,
    false, // !!children,
  );

  const renderDatePicker = () => (
    <Grid
      className="range-grid-block"
      item
      xl={rangeSizes.xl}
      lg={rangeSizes.lg}
      md={rangeSizes.md}
      sm={rangeSizes.sm}
      xs={rangeSizes.xs}
    >
      <DateRangePicker
        disableFuture
        startText="Created from"
        endText="Created to"
        value={datePeriods}
        onChange={(v) => {
          setDatePeriods(v);
          console.log(v);
        }}
        className="date-field"
        onFiltersChange={applyFilterChanges}
      />
    </Grid>
  );

  const renderUserFilter = () => (
    <Grid
      item
      xl={userFilterSizes.xl}
      lg={userFilterSizes.lg}
      md={userFilterSizes.md}
      sm={userFilterSizes.sm}
      xs={userFilterSizes.xs}
    >
      <UserFilter
        filtersValue={
          is.array(filters.filterByUsers)
            ? filters.filterByUsers[0]
            : filters.filterByUsers
        }
        className="text-field"
        onFiltersChange={(user: string) =>
          applyFilterChanges({ filterByUsers: user }, 'filterByUsers')
        }
      />
    </Grid>
  );

  const renderPublisherFilter = () => (
    <Grid
      item
      xl={userFilterSizes.xl}
      lg={userFilterSizes.lg}
      md={userFilterSizes.md}
      sm={userFilterSizes.sm}
      xs={userFilterSizes.xs}
    >
      <PublishersFilter
        filtersValue={filters.publisher}
        className="text-field"
        onFiltersChange={(publisher: string) =>
          applyFilterChanges({ publisher }, 'publisher')
        }
      />
    </Grid>
  );

  const renderPartnersFilter = () => (
    <Grid
      item
      xl={userFilterSizes.xl}
      lg={userFilterSizes.lg}
      md={userFilterSizes.md}
      sm={userFilterSizes.sm}
      xs={userFilterSizes.xs}
    >
      <PartnerFilter
        filtersValue={
          is.array(filters.partnerIds)
            ? filters.partnerIds[0]
            : filters.partnerIds
        }
        className="text-field"
        onFiltersChange={(partners: string) =>
          applyFilterChanges({ partnerIds: [partners] }, 'partnerIds')
        }
      />
    </Grid>
  );

  const androidVersionFilter = () => (
    <Grid
      item
      xl={periodSizes.xl}
      lg={periodSizes.lg}
      md={periodSizes.md}
      sm={periodSizes.sm}
      xs={periodSizes.xs}
    >
      <SGRocketAutocomplete
        fullWidth
        label="Android version"
        placeholder="All versions"
        options={androidVersions}
        value={androidVersionValues}
        onChange={(v) =>
          applyAndroidVersionsChange({
            androidVersion: v.join(separators.comma),
          })
        }
        limitTags={6}
      />
    </Grid>
  );

  const appVersionFilter = () => (
    <Grid
      item
      xl={periodSizes.xl}
      lg={periodSizes.lg}
      md={periodSizes.md}
      sm={periodSizes.sm}
      xs={periodSizes.xs}
    >
      <SGRocketAutocomplete
        fullWidth
        label="App version"
        placeholder="All versions"
        options={appVersions}
        value={appVersionValues}
        onChange={(v) =>
          applyAppVersionsChange({
            appVersion: v.join(separators.comma),
          })
        }
        limitTags={6}
      />
    </Grid>
  );

  const countriesFilter = () => (
    <Grid
      item
      xl={periodSizes.xl}
      lg={periodSizes.lg}
      md={periodSizes.md}
      sm={periodSizes.sm}
      xs={periodSizes.xs}
    >
      <CountryTierAutocomplete
        options={countries}
        value={countriesValues}
        onChange={(v) =>
          applyFilterChanges({ country: v.join(separators.comma) })
        }
      />
    </Grid>
  );

  const renderStateFilter = () => (
    <Grid
      item
      xl={stateFilterSizes.xl}
      lg={stateFilterSizes.lg}
      md={stateFilterSizes.md}
      sm={stateFilterSizes.sm}
      xs={stateFilterSizes.xs}
    >
      <StateFilter
        state={filters.filterByActive}
        className="text-field"
        onFiltersChange={applyFilterChanges}
      />
    </Grid>
  );

  return (
    <div css={style}>
      {title && (
        <Typography style={{ marginBottom: '24px' }} variant="h4">
          {title}
        </Typography>
      )}

      <Grid container spacing={2} alignContent="flex-end">
        <Grid
          item
          xl={periodSizes.xl}
          lg={periodSizes.lg}
          md={periodSizes.md}
          sm={periodSizes.sm}
          xs={periodSizes.xs}
        >
          <GtSelect
            fullWidth
            className="text-field"
            variant="outlined"
            size="small"
            label="Period filter"
            disabled={false}
            value={period}
            onChange={onPeriodChange}
            name={UserField.Role}
            inputProps={{
              name: 'filter',
              id: 'filter',
            }}
          >
            {periodOptionsMap.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </GtSelect>
        </Grid>

        {withDatePicker && renderDatePicker()}
        {withFilterByUser && renderUserFilter()}
        {withFilterByPublisher && renderPublisherFilter()}
        {withFilterByPartner && renderPartnersFilter()}
        {withFilterByState && renderStateFilter()}

        {androidVersions && androidVersionFilter()}
        {appVersions && appVersionFilter()}
        {countries && countriesFilter()}

        {children && (
          <Grid item style={{ flexGrow: 1 }} className="align-right">
            {children}
          </Grid>
        )}
      </Grid>
    </div>
  );
}
